/**
 * A captured photograph to grey levels.
 *
 * This is the one step of the pipeline that is the *platform's* rather than the
 * core's: a browser has a canvas, a phone's device plugin has its own buffers, and
 * neither is arithmetic worth sharing. What matters is the contract it hands on -
 * a `GreyImage` in the layout the core expects, with the same luma weights the
 * catalog's builder uses (`sharp`'s Rec. 601-ish conversion, 0.299/0.587/0.114),
 * because a probe compared with fingerprints built from those levels has to be
 * built the same way.
 *
 * The capture is downscaled on the way in. A phone hands over 12 megapixels and
 * the detector wants a card that is a few hundred pixels wide: resampling once,
 * in the canvas, is both the fastest and the least lossy place to do it.
 */

import type { GreyImage } from '@unsorted/shared';

export interface DecodeOptions {
  /** Longest edge of the grey image the detector sees. */
  maxEdge?: number;
}

/** 900 is well inside what a card needs to be found and probed, and quick. */
export const DEFAULT_MAX_EDGE = 900;

/**
 * RGBA bytes to one level per pixel, with the luma weights the catalog was built with.
 *
 * Shared with the live scanner, which reads the viewfinder's canvas directly: a card
 * captured by auto-fire and a card uploaded from the camera roll have to be the same
 * grey image, or one of them is fingerprinted from pixels the index never saw.
 */
export function greyLevelsFromRgba(data: Uint8ClampedArray, width: number, height: number): GreyImage {
  const levels = new Uint8Array(width * height);
  for (let index = 0; index < levels.length; index += 1) {
    const r = data[index * 4] ?? 0;
    const g = data[index * 4 + 1] ?? 0;
    const b = data[index * 4 + 2] ?? 0;
    levels[index] = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
  }
  return { levels, width, height };
}

export async function decodeCapture(file: Blob, options: DecodeOptions = {}): Promise<GreyImage> {
  const maxEdge = options.maxEdge ?? DEFAULT_MAX_EDGE;
  const url = URL.createObjectURL(file);
  try {
    const image = await loadImage(url);
    const naturalWidth = image.naturalWidth || image.width;
    const naturalHeight = image.naturalHeight || image.height;
    if (naturalWidth < 1 || naturalHeight < 1) throw new Error('that image has no pixels');

    const scale = Math.min(1, maxEdge / Math.max(naturalWidth, naturalHeight));
    const width = Math.max(1, Math.round(naturalWidth * scale));
    const height = Math.max(1, Math.round(naturalHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (context === null) throw new Error('this browser gave the page no 2D canvas to read the capture with');
    context.drawImage(image, 0, 0, width, height);
    const { data } = context.getImageData(0, 0, width, height);
    return greyLevelsFromRgba(data, width, height);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('that file could not be decoded as an image'));
    image.src = url;
  });
}
